import React, { createContext, useContext, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useNumpadShortcutsDisabled } from '@utils/hooks/use-numpad-shortcuts-disabled.ts';

interface TranscriptHotkeyContextType {
  shortcutsModalOpen: boolean;
  openShortcutsModal: () => void;
  closeShortcutsModal: () => void;
  searchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
}

const TranscriptHotkeyContext = createContext<
  TranscriptHotkeyContextType | undefined
>(undefined);

export function useTranscriptHotkeys() {
  const ctx = useContext(TranscriptHotkeyContext);
  if (!ctx)
    throw new Error(
      'useTranscriptHotkeys must be used within TranscriptHotkeyProvider'
    );
  return ctx;
}

interface TranscriptHotkeyProviderProps {
  children: React.ReactNode;
}

export function TranscriptHotkeyProvider({
  children
}: TranscriptHotkeyProviderProps) {
  const { play, pause, isPlaying, rewind, forward, toggleLinkedPlayback } =
    useAudio();

  const [searchOpen, setSearchOpen] = useState(false);

  // #4029 — Profile → Keyboard flag applies here too so numpad 4 (the
  // apostrophe-adjacent key on French AZERTY layouts) doesn't hijack
  // transcript typing when the user has explicitly opted out.
  const numpadDisabled = useNumpadShortcutsDisabled();

  // Discoverable keyboard-shortcut overlay (#1920). Open with ? (Shift + /)
  // anywhere in the editor; the toolbar also exposes a help button so the
  // shortcut itself is discoverable.
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
  useHotkeys(
    ['shift+/', '?'],
    event => {
      event.preventDefault();
      setShortcutsModalOpen(true);
    },
    { enableOnFormTags: false, enableOnContentEditable: false }
  );

  // Play/Pause hotkeys: Covering various keyboard setups, including dedicated multimedia keys
  // Note: F5/F7/F8 are handled separately below with enableOnContentEditable
  useHotkeys(
    [
      'space', // Spacebar
      'f4', // Older keyboards
      'f10', // HP laptops
      'f11', // Some keyboards
      'media_play_pause', // General multimedia key
      'play', // ThinkPad-specific Play key
      'pause', // ThinkPad-specific Pause key
      'audio_play' // Another ThinkPad-specific multimedia key
    ],
    event => {
      event.preventDefault();
      if (isPlaying) {
        pause();
      } else {
        play();
      }
    }
  );

  // Numpad 5 play/pause. `numpad5` also matches the top-row 5 (and "(" on
  // AZERTY), so check the physical key; off when the user opted out.
  useHotkeys('numpad5', event => {
    if (numpadDisabled || event.code !== 'Numpad5') return;
    event.preventDefault();
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  });

  // F5 Play/Pause - works while editing (contentEditable)
  useHotkeys(
    ['f5'],
    event => {
      event.preventDefault();
      if (isPlaying) {
        pause();
      } else {
        play();
      }
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // Ctrl+Space Play/Pause - reliable modifier variant that works while editing
  // transcript text (plain Space types a space inside contentEditable).
  useHotkeys(
    ['ctrl+space', 'meta+space'],
    event => {
      event.preventDefault();
      if (isPlaying) {
        pause();
      } else {
        play();
      }
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // F7 Rewind 5 seconds - works while editing (contentEditable)
  useHotkeys(
    ['f7'],
    event => {
      event.preventDefault();
      rewind(5);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // F8 Forward 5 seconds - works while editing (contentEditable)
  useHotkeys(
    ['f8'],
    event => {
      event.preventDefault();
      forward(5);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );
  // option or ctrl + arrow left rewind 5 seconds
  useHotkeys(
    ['left', 'ctrl+left', 'meta+left'], // 'meta+left' is for Mac
    () => {
      rewind(5);
    }
  );

  // option or ctrl + right arrow forward 5 seconds
  useHotkeys(
    ['right', 'ctrl+right', 'meta+right'], // 'meta+right' is for Mac
    () => {
      forward(5);
    }
  );

  // Playback-rate keyboard shortcuts have been removed. They previously bound
  // ctrl+plus / ctrl+= / ctrl+minus (and meta+…) and called preventDefault(),
  // which blocked the browser's native zoom (Utrecht #2XXX — Cladder/Stoffels
  // couldn't Ctrl+- to zoom out the page, it changed the playback speed
  // instead). Users can adjust playback rate via the dropdown in the media
  // controls.
  // ctrl+f opens the Find & Replace popover (#2233).
  useHotkeys(
    ['ctrl+f', 'meta+f'],
    event => {
      event.preventDefault();
      setSearchOpen(true);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // ctrl+l toggle linked playback
  useHotkeys(
    ['ctrl+l', 'meta+l'],
    event => {
      event.preventDefault();
      toggleLinkedPlayback();
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  return (
    <TranscriptHotkeyContext.Provider
      value={{
        shortcutsModalOpen,
        openShortcutsModal: () => setShortcutsModalOpen(true),
        closeShortcutsModal: () => setShortcutsModalOpen(false),
        searchOpen,
        openSearch: () => setSearchOpen(true),
        closeSearch: () => setSearchOpen(false)
      }}
    >
      {children}
    </TranscriptHotkeyContext.Provider>
  );
}
